function parseMutationError(errors) {
  return errors?.submit || errors?.base || Object.values(errors || {})[0];
}

async function ensureReservation({
  createInboundReservation,
  plan,
  project,
}) {
  if (plan.type === "existing") {
    return {
      success: true,
      inventoryRequest: {
        id: plan.inventoryRequestId,
        workOrderNumber: plan.workOrderNumber,
        comment: plan.inventoryRequestComment || plan.comment,
      },
    };
  }

  let reservationError = null;
  const response = await createInboundReservation({
    variables: {
      projectId: project.id,
      comment: plan.comment,
    },
    onFailure: errors => {
      reservationError = parseMutationError(errors);
    },
  });

  if (reservationError) {
    return { success: false, error: reservationError };
  }

  const payload = response?.data?.createInboundReservation;
  const inventoryRequest = payload?.inventoryRequest;

  if (payload?.errors?.length > 0) {
    return {
      success: false,
      error:
        payload.errors[0]?.message ||
        "We couldn't create the reservation. Please try again.",
    };
  }

  if (!inventoryRequest?.id) {
    return {
      success: false,
      error: "We couldn't create the reservation. Please try again.",
    };
  }

  return { success: true, inventoryRequest };
}

async function addShipmentItemsToReservation({
  addInventoryItem,
  entries,
  inventoryRequestId,
}) {
  const appliedEntries = [];

  for (const entry of entries) {
    let addError = null;
    const response = await addInventoryItem({
      variables: {
        assetId: entry.shipmentItem.asset.id,
        inventoryRequestId,
        quantity: entry.quantityToAdd,
        pickedStatus: false,
      },
      onFailure: errors => {
        addError = parseMutationError(errors);
      },
    });

    if (addError) {
      return { success: false, error: addError, appliedEntries };
    }

    const payload = response?.data?.addInventoryItem;
    if (payload?.errors?.length > 0) {
      return {
        success: false,
        appliedEntries,
        error:
          payload.errors[0]?.message ||
          "We couldn't add the received items to the reservation. Please try again.",
      };
    }

    appliedEntries.push(entry);
  }

  return { success: true, appliedEntries };
}

export async function applyInboundReservation({
  addInventoryItem,
  createInboundReservation,
  project,
  plan,
  shipmentItems = [],
}) {
  const reservationResult = await ensureReservation({
    createInboundReservation,
    plan,
    project,
  });

  if (!reservationResult.success) {
    return reservationResult;
  }

  if (shipmentItems.length === 0) {
    return {
      ...reservationResult,
      appliedEntries: [],
    };
  }

  const addResult = await addShipmentItemsToReservation({
    addInventoryItem,
    entries: shipmentItems,
    inventoryRequestId: reservationResult.inventoryRequest.id,
  });

  if (!addResult.success) {
    return {
      success: false,
      error: addResult.error,
      inventoryRequest: reservationResult.inventoryRequest,
      appliedEntries: addResult.appliedEntries || [],
    };
  }

  return {
    ...reservationResult,
    appliedEntries: addResult.appliedEntries || [],
  };
}

export function buildAppliedReservationPlan(plan, inventoryRequest, appliedShipmentItems) {
  return {
    type: "existing",
    inventoryRequestId: inventoryRequest.id,
    workOrderNumber: inventoryRequest.workOrderNumber,
    inventoryRequestComment: inventoryRequest.comment,
    comment: plan.type === "new" ? plan.comment : plan.inventoryRequestComment,
    applied: true,
    appliedShipmentItems,
  };
}
